import React, { useMemo, Fragment, useState, useEffect } from "react"
import { Container, Col, Row, Button, Card, CardBody, Table } from "reactstrap"
import { Link, useNavigate } from "react-router-dom"
import { useQuery } from "react-query"
import { useTable, useAsyncDebounce } from "react-table"
//Import Breadcrumb
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { GENERAL } from "../../../api/general"
import {
  API_RECORD_LIST_SIZE,
  ROLES,
  USER_STATUS,
} from "helpers/global_variables.helper"
import useDebounce from "helpers/hooks/useDebounce"
import ActivityLoader from "components/activity-loader"
import PaginationComponent from "components/Common/pagination"

const SuperAdminAgentView = props => {
  const navigate = useNavigate()
  const [searchFilter, setSearchFilter] = React.useState("")
  const { value: searchQuery } = useDebounce(searchFilter, 1000)
  const [currentIndex, setCurrentIndex] = useState(1)

  useEffect(() => {
    setCurrentIndex(1)
  }, [searchFilter])

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "get_agents_list",
      {
        searchQuery,
        currentIndex,
      },
    ],
    queryFn: () => {
      return GENERAL.getUsersByRole({
        page: currentIndex,
        role: ROLES.agent,
        search: searchQuery,
        take: API_RECORD_LIST_SIZE,
      })
    },
  })

  //meta title
  document.title = "Agents Registration - eRecruitement"

  const getSns = useMemo(() => {
    const offset = (currentIndex - 1) * API_RECORD_LIST_SIZE
    return data?.records?.map((_, index) => index + offset + 1)
  }, [data, currentIndex, API_RECORD_LIST_SIZE])

  const columns = useMemo(
    () => [
      {
        Header: "S/N",
        Cell: cell => {
          return <p>{getSns[cell.row.index]}</p>
        },
      },
      {
        Header: "Full Name",
        accessor: "name",
        filterable: false,
        disableFilters: true,
        Cell: cellProps => {
          return <FormatBold {...cellProps} />
        },
      },
      {
        Header: "NIN",
        accessor: "nin",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          return <FieldFormat {...cellProps} />
        },
      },
      {
        Header: "Phone No.",
        accessor: "phoneNumber",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          return <FieldFormat {...cellProps} />
        },
      },
      {
        Header: "Email Address.",
        accessor: "email",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          return <FieldFormat {...cellProps} />
        },
      },
      {
        Header: "State of Residence",
        accessor: "stateOfOrigin",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          return <FieldFormat {...cellProps} />
        },
      },
      {
        Header: "LGA of Residence",
        accessor: "lgaOfOrigin",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          return <FieldFormat {...cellProps} />
        },
      },
      {
        Header: "Address",
        accessor: "adress",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          return <FieldFormat {...cellProps} />
        },
      },
      {
        Header: "Total Registrations",
        accessor: "totalRegistrations",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          return <p>{cellProps.value.toString()}</p>
        },
      },
      {
        Header: "Status",
        accessor: "status",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => {
          const status = cellProps?.value
          let buttonVariant = ""
          let buttonText = ""
          switch (status) {
            case USER_STATUS.NEW:
              buttonVariant = "success"
              buttonText = "Active"
              break
            case USER_STATUS.ACTIVE:
              buttonVariant = "success"
              buttonText = "Active"
              break
            case USER_STATUS.LOCKED:
              buttonVariant = "danger"
              buttonText = "Inactive"
              break
            default:
              buttonVariant = "success"
              buttonText = ""
          }

          return (
            <Button
              type="button"
              color={buttonVariant}
              className="btn-sm btn-rounded"
            >
              {buttonText}
            </Button>
          )
        },
      },

      {
        Header: "View Details",
        disableFilters: true,
        accessor: "view",
        Cell: cellProps => {
          return (
            <Button
              type="button"
              color="primary"
              className="btn-sm btn-rounded"
              onClick={() => {
                gotoProfile(cellProps)
              }}
            >
              View Details
            </Button>
          )
        },
      },
    ],
    [currentIndex, data, API_RECORD_LIST_SIZE]
  )

  const gotoProfile = record => {
    navigate(`/agents/${record?.cell?.row?.original.id}`)
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { pageIndex, pageSize },
  } = useTable({
    columns,
    data: data?.records ?? [],
    initialState: {
      sortBy: [
        {
          desc: true,
        },
      ],
    },
  })

  const FormatBold = cell => {
    return (
      <Link to="#" className="text-body fw-bold">
        {cell.value ? cell.value : ""}
      </Link>
    )
  }

  const FieldFormat = cell => {
    return cell.value ? cell.value : ""
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Row>
            <Breadcrumbs parentLink="All Agents" hasBackBtn={true} />
          </Row>

          <Card>
            <CardBody>
              <div className="mb-4 h4 card-title">
                All Agents - {data?.total}
              </div>
              <Fragment>
                <Row className="mb-2">
                  <React.Fragment>
                    <Col md={6}>
                      <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
                        <div
                          className="position-relative"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <label
                            htmlFor="search-bar-0"
                            className="search-label"
                          >
                            <span id="search-bar-0-label" className="sr-only">
                              Search this table
                            </span>
                            <input
                              onChange={e => {
                                // setValue(e.target.value)
                                setSearchFilter(e.target.value)
                              }}
                              id="search-bar-0"
                              type="text"
                              className="form-control"
                              placeholder={`search...`}
                            />
                          </label>
                          <i
                            className="bx bx-search-alt search-icon"
                            style={{ cursor: "pointer" }}
                          ></i>
                        </div>
                      </div>
                    </Col>
                  </React.Fragment>
                </Row>

                <div className="table-responsive react-table">
                  <Table bordered hover {...getTableProps()}>
                    <thead className="table-primary table-nowrap">
                      {headerGroups.map(headerGroup => (
                        <tr
                          key={headerGroup.id}
                          {...headerGroup.getHeaderGroupProps()}
                        >
                          {headerGroup.headers.map(column => (
                            <th key={column.id} {...column.getHeaderProps()}>
                              {column.render("Header")}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                      {isLoading || isFetching ? (
                        <tr>
                          <td colSpan={columns.length} className=" text-center">
                            <ActivityLoader variant="primary" />
                          </td>
                        </tr>
                      ) : rows.length ? (
                        rows?.map((row, i) => {
                          prepareRow(row)
                          return (
                            <tr key={i} {...row.getRowProps()}>
                              {row.cells.map(cell => {
                                return (
                                  <td key={cell.id} {...cell.getCellProps()}>
                                    {cell.render("Cell")}
                                  </td>
                                )
                              })}
                            </tr>
                          )
                        })
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className=" text-center">
                            No results.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                <div className="d-flex justify-content-end align-items-center">
                  <PaginationComponent
                    maxPages={Math.ceil(
                      (data?.total || 0) / API_RECORD_LIST_SIZE
                    )}
                    getCurrentPage={setCurrentIndex}
                    page={currentIndex}
                  />
                </div>
              </Fragment>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default SuperAdminAgentView
